import { isDefined } from "@clipboard-health/util-ts";
import { Diagnostic } from "@ionic-native/diagnostic";
import { HCP_ACTIONS, MISSING_DOCS_TRIGGER } from "@src/app/dayView/constants";
import { GET_MISSING_REQUIREMENTS_FOR_DATE } from "@src/app/documents/gql";
import { GraphQlClient } from "@src/app/graphql";
import { formatRate } from "@src/app/hcpShifts/helper";
import { locationTrackingHelper } from "@src/app/locationTracking/locationTracking.helper";
import { TripTrackingTrigger } from "@src/app/locationTracking/locationTracking.types";
import { getShiftDetailsById } from "@src/app/openShifts/api";
import { UrgencyType } from "@src/app/openShifts/urgentShifts/constants";
import { locationService } from "@src/app/openShifts/urgentShifts/locationService";
import { acceptNegotiation } from "@src/app/rateNegotiation/api";
import { NegotiationStatus } from "@src/app/rateNegotiation/interfaces";
import { AppV2AccountRoutes } from "@src/app/routing/constant/appV2AccountRoutes";
import { useSession } from "@src/app/store/helperHooks";
import { useOpenShiftItem } from "@src/app/store/helperHooks/shiftStoreHelper";
import { addOpenShiftItem, updateOpenShiftItem } from "@src/app/store/shift";
import {
  DOCUMENTS_PASSED_BOOKING_STATUSES,
  OpenShiftItemBookingStatus,
} from "@src/app/store/shift/model";
import { getNonIPAmount } from "@src/app/utils/shiftHelper";
import { showFacilityCovidTestingPopups } from "@src/app/utils/showFacilityCovidTestingPopup";
import { CbhFeatureFlag, useCbhFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { deprecatedDoNotUseLogError, logEvent } from "@src/appV2/lib/analytics";
import { getDeviceGeoLocation } from "@src/appV2/Location";
import { Shift } from "@src/appV2/Shifts/Shift/types";
import { SEARCH_MODE, USER_EVENTS } from "@src/constants";
import { HttpStatus } from "@src/constants/http";
import { logApiFailureEvent } from "@src/lib/analytics";
import { LegacyShift, PaymentServiceAccountStatus } from "@src/lib/interface";
import { ActionType } from "@store/session";
import moment from "moment-timezone";
import { useCallback, useEffect, useReducer } from "react";
import { useDispatch } from "react-redux";
import { generatePath, useHistory, useLocation } from "react-router-dom";

import { UpdateShiftListFn } from "./interfaces";
import { useMissingDocuments } from "./useMissingDocuments";
import { makeInstantpayLogParameters } from "../../../utils/logEvent";
import { getICAgreementStatus } from "../../featureFlag/api";
import { useShouldBeOnboarded } from "../../onboardingStripe/hooks";
import { isLocationEnabledAndHasFullLocationPermission } from "../../openShifts/urgentShifts/utils";
import { TabRouterPath } from "../../routing/constant/tabRoute";
import {
  claimAgentShift,
  fetchAgentProfile,
  fetchInstantBookStatusById,
  markShiftInterest,
} from "../api";
import { ShiftItemInitialBookingStatus } from "../constants";
import { ShiftItemActionType, ShiftItemCategory, UseShiftItemHookReturnType } from "../model";
import { initialShiftItemState, shiftItemReducer } from "../shiftItem/shiftItemReducer";

interface UseShiftItemProps {
  shift: LegacyShift;
  goToDocumentsPage: any;
  isInstantBookingShift?: boolean;
  handleShiftActivityLog: any;
  updateShiftList?: UpdateShiftListFn;
  isUrgentShift?: boolean;
  isLastMinuteShift: boolean;
  validator: any;
  pushNotification: any;
  shiftActivityLog: any;
  searchMode?: SEARCH_MODE;
  availableShiftIds: any;
  filterDistance: any;
  filterDays: any;
  sourceContext: any;
  setClickedInterestInShift?: (value: boolean) => void;
  isHcpInstantPayEnabled?: boolean;
  isRateNegotiationActive?: boolean;
}

/**
 * FIXME this hook needs to be broken up and have a reduced surface area.
 * It has way too many concerns and dependencies. It is also given function callbacks
 * that trigger side-effects, making this complex to manage and generally hard to reason about.
 * Any function that takes 18+ props, and returns dozens is a good indication of too many concerns.
 * It also has numerous untyped values or invalid/mismatching types
 * It is also over a 1000 long!
 */
export function useShiftItem(props: UseShiftItemProps): UseShiftItemHookReturnType {
  const {
    shift,
    goToDocumentsPage,
    isInstantBookingShift,
    handleShiftActivityLog,
    updateShiftList,
    isLastMinuteShift,
    validator,
    pushNotification,
    shiftActivityLog,
    isUrgentShift,
    searchMode,
    availableShiftIds,
    filterDistance,
    filterDays,
    sourceContext,
    setClickedInterestInShift,
    isHcpInstantPayEnabled,
    isRateNegotiationActive,
  } = props;
  const { agent } = useSession();
  const ldFlags = useCbhFlags();
  const [state, dispatch] = useReducer(shiftItemReducer, initialShiftItemState);
  const storeDispatch = useDispatch();

  const isNonQfaRangeShift = searchMode === SEARCH_MODE.NON_QFA_RANGE_SHIFTS;

  // FIXME: This flag is disabled in prod, we should deprecate this flag and logic
  const isCheckShiftRateChangeEnabled = useCbhFlag(
    CbhFeatureFlag.CHECK_RATE_DECREASE_WHEN_BOOKING,
    { defaultValue: false }
  );

  const openShiftItem = useOpenShiftItem(shift._id ?? "") ?? {};

  const { showSuccessToast, showErrorToast } = useToast();

  const {
    cbh,
    state: stateLoc,
    msa,
    county,
    hcf,
    covid,
    pendingDocuments,
    loading: documentsAreLoading,
    missingDocuments,
  } = useMissingDocuments(shift, {
    skip: isNonQfaRangeShift,
  });

  const history = useHistory();

  const preventFromBook = useShouldBeOnboarded();

  const location = useLocation();

  useEffect(() => {
    if (shift?.interested?.find((agentId) => agentId === agent?.userId)) {
      dispatch({ type: ShiftItemActionType.SHOWED_INTEREST });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift?.interested?.length, agent?.userId, shift.agentId, shift.isHCFConfirmed]);

  useEffect(() => {
    if (shift.agentId === agent?.userId) {
      dispatch({ type: ShiftItemActionType.WORK_APPROVED });
    }
  }, [shift.agentId, agent?.userId, shift.isHCFConfirmed]);

  useEffect(() => {
    if (documentsAreLoading) {
      return;
    }

    const payload = { category: ShiftItemCategory.BOOKING_WARNING };

    if (agent?.paymentAccountInfo?.status === PaymentServiceAccountStatus.NO_DOCUMENTS_REQUIRED) {
      payload.category = ShiftItemCategory.NOT_READY_TO_BOOK_SHIFT;
    }

    if (missingDocuments.size > 0 && preventFromBook) {
      payload.category = ShiftItemCategory.STRIPE_NOT_CONNECTED_AND_MISSING_HCP_DOCUMENTS;
      payload["missingDocs"] = missingDocuments;
    } else if (missingDocuments.size > 0) {
      payload.category = ShiftItemCategory.MISSING_CBH_DOCUMENTS;
      payload["missingDocs"] = missingDocuments;
    } else if (pendingDocuments.size) {
      payload.category = ShiftItemCategory.WAITING_FOR_APPROVE;
    }

    if (state.category !== payload.category) {
      dispatch({ type: ShiftItemActionType.SET_CATEGORY, payload });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cbh,
    dispatch,
    documentsAreLoading,
    hcf,
    isInstantBookingShift,
    msa,
    stateLoc,
    county,
    state.interested,
    state.workApproved,
    agent?.paymentAccountInfo?.status,
    pendingDocuments,
    missingDocuments,
  ]);

  useEffect(() => {
    if (pushNotification) {
      dispatch({
        type: ShiftItemActionType.CONFIGURE_PUSH_NOTIFICATION,
        payload: { pushNotification },
      });
      if (pushNotification.isPushSet && state.interested) {
        dispatch({ type: ShiftItemActionType.OPEN_PUSH_NOTIFICATION });
      }
    }
  }, [dispatch, pushNotification, state.interested]);

  const getHcpMissingRequirementsForDate = useCallback(async () => {
    // checking requirements status as of shift end date
    const {
      data: { hcpMissingRequirementsForDate },
    } = await GraphQlClient.query({
      query: GET_MISSING_REQUIREMENTS_FOR_DATE,
      fetchPolicy: "network-only",
      variables: {
        hcfId: shift.facilityId,
        date: shift.end,
        qualification: shift.agentReq,
      },
    });
    return hcpMissingRequirementsForDate;
  }, [shift.facilityId, shift.end, shift.agentReq]);

  const handleOnboardingProcess = useCallback(async () => {
    return history.push(TabRouterPath.PAYMENT_SERVICE_ONBOARDING);
  }, [history]);

  useEffect(() => {
    if (!searchMode) {
      return;
    }
    const bookingStatus = ShiftItemInitialBookingStatus[searchMode];
    storeDispatch(
      /**
       * FIXME: the shift id may be undefined. Evaluate and adjust types to make this safe.
       * @ts-expect-error ts-2322 */
      addOpenShiftItem({ id: shift?._id, types: [searchMode], bookingStatus })
    );
  }, [searchMode, shift._id, storeDispatch]);

  const checkNonQfaRangeShiftDocumentStatus = useCallback(async () => {
    try {
      storeDispatch(
        updateOpenShiftItem({
          /**
           * FIXME: the shift id may be undefined. Evaluate and adjust types to make this safe.
           * @ts-expect-error ts-2322 */
          id: shift._id,
          bookingStatus: OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_CHECKING_DOCUMENT_STATUS,
        })
      );
      const missingRequirements = await getHcpMissingRequirementsForDate();

      const bookingStatus =
        missingRequirements?.length === 0
          ? OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_DOCUMENT_STATUS_PASS
          : OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_DOCUMENT_STATUS_FAIL;

      storeDispatch(
        updateOpenShiftItem({
          /**
           * FIXME: the shift id may be undefined. Evaluate and adjust types to make this safe.
           * @ts-expect-error ts-2322 */
          id: shift._id,
          bookingStatus,
        })
      );
    } catch (error) {
      storeDispatch(
        updateOpenShiftItem({
          /**
           * FIXME: the shift id may be undefined. Evaluate and adjust types to make this safe.
           * @ts-expect-error ts-2322 */
          id: shift._id,
          bookingStatus:
            OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_CHECKING_DOCUMENT_STATUS_ERROR,
        })
      );
      showErrorToast("Error checking if requirements are met, please try again.");
      deprecatedDoNotUseLogError({
        message: "[Browsing Shifts Across Map] Unable to check missing requirements for date",
        metadata: {
          userId: agent?.userId,
          facilityId: shift?.facilityId,
          shiftId: shift._id,
          errorMessage: error?.message,
          stack: error?.stack,
        },
      });
    }
  }, [
    storeDispatch,
    shift._id,
    shift?.facilityId,
    getHcpMissingRequirementsForDate,
    showErrorToast,
    agent?.userId,
  ]);

  const handleNonQfaRangeShiftAction = useCallback(
    async (bookingStatus: OpenShiftItemBookingStatus | undefined) => {
      switch (bookingStatus) {
        case OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_DOCUMENT_STATUS_FAIL:
          history.push(
            generatePath(AppV2AccountRoutes.HCF_DOCUMENT_BY_ID_AND_NAME, {
              hcfId: shift?.facilityId,
              hcfName: shift?.facility?.name,
            }),
            {
              returnUrl: location.pathname,
            }
          );
          break;
        case OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_CHECKING_DOCUMENT_STATUS_ERROR:
        case OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_CHECK_DOCUMENT_STATUS:
          await checkNonQfaRangeShiftDocumentStatus();
          break;
        default:
          return;
      }
    },
    [
      history,
      shift?.facilityId,
      shift?.facility?.name,
      location.pathname,
      checkNonQfaRangeShiftDocumentStatus,
    ]
  );

  const isInstantBookCheckRequired =
    isNonQfaRangeShift &&
    isDefined(openShiftItem.bookingStatus) &&
    DOCUMENTS_PASSED_BOOKING_STATUSES.includes(openShiftItem.bookingStatus);

  const loadAlertDialog = useCallback(
    async (callback) => {
      if (preventFromBook && state.category === ShiftItemCategory.BOOKING_WARNING) {
        return handleOnboardingProcess();
      }

      if (
        isNonQfaRangeShift &&
        openShiftItem?.bookingStatus !==
          OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_DOCUMENT_STATUS_PASS
      ) {
        return handleNonQfaRangeShiftAction(openShiftItem?.bookingStatus);
      }

      const agreementStatus = await getICAgreementStatus(agent?.userId as string);
      if (agreementStatus && agreementStatus.shouldSignAgreement) {
        storeDispatch({ type: ActionType.SHOW_IC_AGREEMENT_POPUP });
      } else {
        if (state.category === ShiftItemCategory.BOOKING_WARNING) {
          dispatch({
            type: ShiftItemActionType.SET_CHECKING_DOCS,
            payload: {
              checkingDocs: true,
            },
          });
          const hcpMissingRequirementsForDate = await getHcpMissingRequirementsForDate();

          dispatch({
            type: ShiftItemActionType.SET_CHECKING_DOCS,
            payload: {
              checkingDocs: false,
            },
          });

          if (hcpMissingRequirementsForDate?.length) {
            const hcpMissingRequirementsForDateSet = new Set(hcpMissingRequirementsForDate);

            dispatch({
              type: ShiftItemActionType.SET_MISSING_DOCS,
              payload: {
                category: preventFromBook
                  ? ShiftItemCategory.STRIPE_NOT_CONNECTED_AND_MISSING_CBH_DOCUMENTS_BEFORE_A_SHIFT_END
                  : ShiftItemCategory.MISSING_CBH_DOCUMENTS_BEFORE_A_SHIFT_END,
                missingDocs: hcpMissingRequirementsForDateSet,
                alertOpen: true,
              },
            });
            logEvent(USER_EVENTS.MISSING_REQUIREMENTS_ALERT_SHOWN, {
              reqIds: [...hcpMissingRequirementsForDateSet].map(
                (missingDoc: any) => missingDoc.reqId
              ),
              action: MISSING_DOCS_TRIGGER.BOOKING_ATTEMPT,
              shiftId: shift?.shiftId,
            });
            return;
          }

          /**
           * Conditional logic to check if shift is instant book.
           * Currently the instant book shift endpoint only returns shift status for
           * shifts in 150 miles radius which can not be used for non qfa shifts.
           * Hence need this check to figure out if shift should be marked as interest or not.
           */
          if (isInstantBookCheckRequired && isDefined(shift.shiftId)) {
            try {
              /**
               * FIXME: should be replaced by useQuery with conditional disability in v2 of HPS.
               * Ticket:-https://clipboardhealth.atlassian.net/browse/CH-22225
               */
              const { isShiftInstantBook } = await fetchInstantBookStatusById(shift.shiftId);
              if (!isShiftInstantBook) {
                dispatch({
                  type: ShiftItemActionType.SET_CATEGORY,
                  payload: {
                    category: ShiftItemCategory.BOOKING_WARNING,
                    isMarkInterest: true,
                  },
                });
                dispatch({ type: ShiftItemActionType.OPEN_ALERT });
                return;
              }
            } catch (error) {
              showErrorToast("Error checking instant book status for the shift, please try again.");
              deprecatedDoNotUseLogError({
                message: "Error checking instant book status for the shift.",
                metadata: {
                  userId: agent?.userId,
                  facilityId: shift?.facilityId,
                  shiftId: shift._id,
                  errorMessage: error?.message,
                  stack: error?.stack,
                },
              });
              return;
            }
          }

          if (
            typeof callback === "function" &&
            state.category === ShiftItemCategory.BOOKING_WARNING
          ) {
            callback();
            return;
          }

          const isLocationPermissionRequired = await checkLocationPermission();
          if (isLocationPermissionRequired) {
            dispatch({
              type: ShiftItemActionType.OPEN_LOCATION_PRIMER_PAGE,
              payload: { category: ShiftItemCategory.NO_LOCATION_PERMISSION },
            });
            return;
          }

          // FIXME: This flag is disabled in prod, we should deprecate this flag and logic
          if (isCheckShiftRateChangeEnabled) {
            /**
             * FIXME: the shift id may be undefined. Evaluate and adjust types to make this safe.
             * @ts-expect-error ts-2322 */
            const freshShiftDetails = await getShiftDetailsById(shift.shiftId);
            const changedShiftPayText = getChangedShiftPayText(freshShiftDetails);
            if (changedShiftPayText) {
              logEvent(USER_EVENTS.RATE_DECREASE_ALERT_SHOWN, {
                shiftId: shift.shiftId,
                displayed_pay: shift.finalPay,
                actual_pay: freshShiftDetails.finalPay,
                agentReq: shift.agentReq,
                msa: shift.facility?.fullAddress?.metropolitanStatisticalArea,
              });
              // updating the pay values only for this shift instance instead of the state because updating the state rerenders everything and closes modals
              shift.originalAmount = freshShiftDetails.originalAmount;
              shift.finalPay = freshShiftDetails.finalPay;
              dispatch({
                type: ShiftItemActionType.SET_SHIFT_PAY_CHANGED,
                payload: {
                  category: ShiftItemCategory.SHIFT_PAY_CHANGED,
                  changedShiftPayText: changedShiftPayText,
                  alertOpen: true,
                },
              });
              return;
            }
          }
        }

        dispatch({ type: ShiftItemActionType.OPEN_ALERT });
      }
      //SEGMENT LOG
      switch (state.category) {
        case ShiftItemCategory.MISSING_CBH_DOCUMENTS:
          logEvent(USER_EVENTS.TAPPED_UPLOAD_REQUIRED_DOCS, {
            missingDocs: missingDocuments.size
              ? [...missingDocuments.values()].map((i) => i.name)
              : "",
            pending: pendingDocuments.size ? [...pendingDocuments.values()] : "",
          });
          logEvent(USER_EVENTS.MISSING_REQUIREMENTS_ALERT_SHOWN, {
            reqIds: [...missingDocuments].map((missingDoc: any) => missingDoc.reqId),
            action: MISSING_DOCS_TRIGGER.MISSING_DOCUMENTS,
            shiftId: shift?.shiftId,
          });
          break;
      }
    },
    // FIXME: Fix Dependency array and perform regression of shift booking via all flows eg. qfa, day view
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      preventFromBook,
      state,
      handleOnboardingProcess,
      handleNonQfaRangeShiftAction,
      openShiftItem?.bookingStatus,
      isInstantBookCheckRequired,
    ]
  );

  const handleClickUploadDocs = (): void => {
    switch (state.category) {
      case ShiftItemCategory.MISSING_CBH_DOCUMENTS:
        logEvent(USER_EVENTS.TAPPED_UPLOAD_MISSING_DOCS_POPUP_BUTTON);
        break;
    }
    goToDocumentsPage(shift);
  };

  const setCategoryToWelcomeToInstantPayAndOpenInstantPayAlert = () => {
    if (!shift.isHCFInstantPayProhibited) {
      dispatch({
        type: ShiftItemActionType.SET_CATEGORY,
        payload: { category: ShiftItemCategory.WELCOME_TO_INSTANTPAY },
      });
      dispatch({ type: ShiftItemActionType.OPEN_ALERT });
    }
  };

  const showOverworkBookingAlert = () => {
    dispatch({
      type: ShiftItemActionType.SET_CATEGORY,
      payload: { category: ShiftItemCategory.OVERWORK_BOOKING },
    });
    dispatch({ type: ShiftItemActionType.OPEN_ALERT });
  };

  const instantBookShift = async (): Promise<void> => {
    if (validator && !validator(shift)) {
      return;
    }

    dispatch({ type: ShiftItemActionType.SUBMITTING });
    const shiftId = shift.shiftId || shift._id;
    const offerId = shift.offer?.id;

    try {
      let latitude: number | undefined = undefined;
      let longitude: number | undefined = undefined;

      if (shift.urgency === UrgencyType.NCNS) {
        try {
          const locationAvailable = await Diagnostic.isLocationAvailable();

          if (locationAvailable) {
            const { geoLocation } = await getDeviceGeoLocation();

            ({ latitude, longitude } = geoLocation);
          } else {
            const coords = await locationService.getGeoCodeAddress(agent?.address?.formatted ?? "");
            if (coords) {
              ({ latitude, longitude } = coords);
            }
          }
        } catch (error) {
          logEvent(USER_EVENTS.GET_CURRENT_POSITION_FAILED, {
            shiftId,
            shiftStart: shift.start,
            workerId: agent?.userId,
            reason: "Failed getting current position on booking urgent shift",
          });
        }
      }

      let apiShiftResponse: LegacyShift;

      if (
        shift?.rateNegotiation?.status === NegotiationStatus.OPEN &&
        shift?.rateNegotiation?.history?.length > 1 &&
        isRateNegotiationActive
      ) {
        const { bookedShift } = await acceptNegotiation(shift?.rateNegotiation._id);
        apiShiftResponse = bookedShift;
      } else {
        apiShiftResponse = await claimAgentShift({
          /**
           * FIXME: the shift id may be undefined. Evaluate and adjust types to make this safe.
           * @ts-expect-error ts-2322 */
          shiftId,
          offerId,
          hasLastMinuteGrabCheck: isLastMinuteShift,
          longitude,
          latitude,
          searchMode,
          availableShifts: availableShiftIds,
          filterDistance,
          filterDays,
        });
      }
      setClickedInterestInShift?.(true);
      logEvent(USER_EVENTS.BOOK_INSTANTBOOK_SHIFT, {
        ...makeInstantpayLogParameters(
          shift,
          isHcpInstantPayEnabled && shift?.isHCFInstantPayProhibited === false,
          agent
        ),
        instant_book: true,
        last_minute: false,
        hcpId: agent?.userId as string,
        email: agent?.email as string,
        context: sourceContext,
        workplaceId: shift?.facilityId,
        facilityType: shift?.facility?.type,
      });
      /**
       * FIXME: the shift facility may be undefined. Evaluate and adjust types to make this safe.
       * @ts-expect-error ts-2322 */
      showFacilityCovidTestingPopups(shift.facility, storeDispatch);
      updateShiftList?.(shift as Shift, true, apiShiftResponse);
      if (!isLastMinuteShift && handleShiftActivityLog) {
        handleShiftActivityLog(shiftId, HCP_ACTIONS.SELF_CLAIM);
      }

      if (shift.urgency === UrgencyType.NCNS) {
        try {
          await locationTrackingHelper.handleTrackingModeChange({
            agentId: agent?.userId as string,
            trigger: TripTrackingTrigger.urgentShiftBooking,
          });
        } catch (error) {
          logEvent(USER_EVENTS.TRIP_TRACKING_ERROR, {
            shiftId,
            shiftStart: shift.start,
            workerId: agent?.userId,
            error: error.message,
          });
        }
      }
      dispatch({ type: ShiftItemActionType.SUBMITTED });
    } catch (error) {
      dispatch({ type: ShiftItemActionType.ERROR_SUBMITTING });
      if (error?.response?.body?.errorType === ShiftItemCategory.OVERWORK_BOOKING) {
        showOverworkBookingAlert();
        throw error;
      }
      if (error?.status === HttpStatus.CONFLICT) {
        updateShiftList?.(shift as Shift, true, null, error);
      }
      const errorMessage =
        error?.response?.body?.displayableMessage ??
        error?.response?.body?.message ??
        "Unable to claim this shift.";
      showErrorToast(errorMessage);
      storeDispatch({ type: ActionType.HIDE_COVID_TESTING_POPUP });
      throw error;
    }
  };

  const bookShift = async (): Promise<void> => {
    /**
     * Validation like shift conflicts with already booked shifts are evaluated here.
     */
    if (validator && !validator(shift)) {
      return;
    }
    dispatch({ type: ShiftItemActionType.SUBMITTING });

    try {
      const activityLog = isLastMinuteShift ? {} : shiftActivityLog(shift._id);
      /**
       * FIXME: the shift id may be undefined. Evaluate and adjust types to make this safe.
       * @ts-expect-error ts-2322 */
      const response = await markShiftInterest(shift._id, activityLog);

      setClickedInterestInShift?.(true);

      if (response.error) {
        dispatch({ type: ShiftItemActionType.ERROR_SUBMITTING });

        showErrorToast(response.error || "Cannot book this shift");
      }
      /**
       * FIXME: the shift facility may be undefined. Evaluate and adjust types to make this safe.
       * @ts-expect-error ts-2322 */
      showFacilityCovidTestingPopups(shift.facility, storeDispatch);

      if (!agent?.interestedAt) {
        const response = await fetchAgentProfile();
        if (response) {
          storeDispatch({
            type: ActionType.UPDATE_AGENT,
            data: { agent: response },
          });
        }
      } else {
        dispatch({ type: ShiftItemActionType.OPEN_PUSH_NOTIFICATION });
      }

      if (!agent?.notificationPreferences?.push) {
        showSuccessToast("Your booking has been noted, a staffer will be in touch.");
      }
      if (isLastMinuteShift) {
        logEvent(USER_EVENTS.SUCCESSFULLY_BOOKED_LAST_MINUTE_SHIFT, {
          hcpId: agent?.userId as string,
          email: agent?.email as string,
          hcpName: agent?.name as string,
          hcpQualification: agent?.preference?.qualification as string,
          shiftName: shift.name,
          shiftId: shift._id,
          shiftPay: shift.finalPay,
          facilityName: shift.facility?.name,
          startAt: shift.start,
          endAt: shift.end,
          timestamp: moment().format(),
          instant_book: false,
          last_minute: true,
          instant_pay: isHcpInstantPayEnabled && shift?.isHCFInstantPayProhibited === false,
          context: sourceContext,
          workplaceId: shift.facilityId,
          facilityType: shift?.facility?.type,
        });
      } else {
        // Below data is required to send slack notifications.
        logEvent(USER_EVENTS.MARKED_INTEREST_IN_SHIFT, {
          status: response.agentHasAllDocuments ? "success" : "missing docs",
          hcpUserId: agent?.userId as string,
          hcpName: agent?.name as string,
          shiftId: shift._id,
          hcpQualification: agent?.preference?.qualification as string,
          shiftName: shift.name,
          facilityName: shift.facility?.name,
          facilityId: shift.facility?._id,
          startAt: shift.start,
          endAt: shift.end,
          missingDocReqIds: JSON.stringify(Array.from(missingDocuments).map((doc) => doc.reqId)),
          missingDocNames: JSON.stringify(Array.from(missingDocuments).map((doc) => doc.name)),
          timestamp: moment().format(),
        });
        logEvent(USER_EVENTS.BOOKED_A_SHIFT, {
          instant_book: false,
          last_minute: false,
          instant_pay: isHcpInstantPayEnabled && shift?.isHCFInstantPayProhibited === false,
          workplaceId: shift?.facilityId,
          context: sourceContext,
          facilityType: shift?.facility?.type,
        });
      }
      dispatch({ type: ShiftItemActionType.SUBMITTED });
      updateShiftList?.(shift as Shift, false, response);
    } catch (err) {
      dispatch({ type: ShiftItemActionType.ERROR_SUBMITTING });
      storeDispatch({ type: ActionType.HIDE_COVID_TESTING_POPUP });
    }
  };

  const onConfirm = async (): Promise<void> => {
    dispatch({ type: ShiftItemActionType.CLOSE_ALERT });
    dispatch({ type: ShiftItemActionType.REMOVE_MARK_INTEREST_FLAG });
    try {
      switch (state.category) {
        case ShiftItemCategory.BOOKING_WARNING:
          if (isNonQfaRangeShift) {
            if (state.isMarkInterest) {
              bookShift();
              break;
            }
            await instantBookShift();
            if (!shift.isHCFInstantPayProhibited) {
              setCategoryToWelcomeToInstantPayAndOpenInstantPayAlert();
            }
            break;
          }
          if (isInstantBookingShift && !shift.isHCFInstantPayProhibited) {
            await instantBookShift();
            setCategoryToWelcomeToInstantPayAndOpenInstantPayAlert();
          } else if (isInstantBookingShift) {
            await instantBookShift();
          } else {
            bookShift();
          }
          break;
        case ShiftItemCategory.MISSING_CBH_DOCUMENTS:
        case ShiftItemCategory.MISSING_CBH_DOCUMENTS_BEFORE_A_SHIFT_END:
        case ShiftItemCategory.NOT_READY_TO_BOOK_SHIFT:
        case ShiftItemCategory.WAITING_FOR_APPROVE:
        case ShiftItemCategory.STRIPE_NOT_CONNECTED_AND_MISSING_HCP_DOCUMENTS:
        case ShiftItemCategory.STRIPE_NOT_CONNECTED_AND_MISSING_CBH_DOCUMENTS_BEFORE_A_SHIFT_END:
          handleClickUploadDocs();
          break;
        case ShiftItemCategory.WELCOME_TO_INSTANTPAY:
          if (isInstantBookingShift) {
            await instantBookShift();
          }
          break;
        case ShiftItemCategory.SHIFT_PAY_CHANGED:
          dispatch({
            type: ShiftItemActionType.SET_CATEGORY,
            payload: {
              category: ShiftItemCategory.BOOKING_WARNING,
              alertOpen: true,
            },
          });
          break;
      }
    } catch (error) {
      logApiFailureEvent(error);
    }
  };

  const onCancel = useCallback(() => {
    dispatch({ type: ShiftItemActionType.CLOSE_ALERT });
    switch (state.category) {
      case ShiftItemCategory.SHIFT_PAY_CHANGED:
        dispatch({
          type: ShiftItemActionType.SET_CATEGORY,
          payload: {
            category: ShiftItemCategory.BOOKING_WARNING,
            isMarkInterest: false,
          },
        });
        break;
    }
  }, [dispatch, state.category]);

  const closePushNotification = useCallback(() => {
    dispatch({ type: ShiftItemActionType.CLOSE_PUSH_NOTIFICATION });
  }, [dispatch]);

  const onClosePrimerPage = useCallback(() => {
    dispatch({
      type: ShiftItemActionType.CLOSE_LOCATION_PRIMER_PAGE,
      payload: { category: ShiftItemCategory.BOOKING_WARNING },
    });
  }, [dispatch]);

  const checkLocationPermission = async () => {
    if (!isCapacitorPlatform()) {
      return false;
    }

    const isAlwaysAllowedGiven = await isLocationEnabledAndHasFullLocationPermission();

    return !isAlwaysAllowedGiven;
  };

  const getChangedShiftPayText = (freshShiftDetails?: LegacyShift) => {
    let changedShiftPayText: string | undefined;
    if (!freshShiftDetails) {
      return changedShiftPayText;
    }
    const originalAmount = getNonIPAmount(shift, ldFlags);
    const newTotalAmount = getNonIPAmount(freshShiftDetails, ldFlags);
    if (isUrgentShift && newTotalAmount < originalAmount) {
      changedShiftPayText = `${formatRate(newTotalAmount)} total earnings`;
    } else if (
      freshShiftDetails.finalPay &&
      isDefined(shift.finalPay) &&
      freshShiftDetails.finalPay < shift.finalPay
    ) {
      changedShiftPayText = `${formatRate(freshShiftDetails.finalPay || 0)} per hour`;
    }
    return changedShiftPayText;
  };

  return {
    ...state,
    loadAlertDialog,
    onConfirm,
    onCancel,
    covid,
    loading: documentsAreLoading,
    closePushNotification,
    onClosePrimerPage,
  };
}
